import { PhysicalAssetType } from 'apps/user/types/on-boarding.type';
import { useCreatePhysicalAsset } from 'common/queries-and-mutations/physical-asset';
import Button from 'components/Form/Button';
import CustomSelect from 'components/Form/CustomSelect';
import TextField from 'components/Form/TextField';
import { Icon, Icons } from 'components/Icon';
import { InfoModal } from 'components/Modal';
import { Alert } from 'components/Toast';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
};

export default function AddPhysicalAssetModal({ onClose, onSuccess }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate, isSuccess, isLoading, isError } = useCreatePhysicalAsset();

  const formik = useFormik({
    initialValues: {
      physicalAsset: [
        {
          physicalAssetId: null,
          assetType: '',
          description: '',
          location: '',
          regNo: ''
        }
      ]
    },
    validationSchema: Yup.object({
      physicalAsset: Yup.array().of(
        Yup.object().shape({
          assetType: Yup.string().required('Asset type is required').nullable(),
          description: Yup.string()
            .required('Description is required')
            .nullable(),
          // location: Yup.string()
          //   .required('Location of the asset is required')
          //   .nullable(),
          regNo: Yup.string().required('Asset No is required')
        })
      )
    }),
    enableReinitialize: true,
    onSubmit: (values, { setFieldTouched }) => {
      setIsSubmitting(true);
      for (const key in values) {
        setFieldTouched(key, true);
      }
      onPhysicalSubmit(values);
    }
  });

  const onPhysicalSubmit = (formValue: PhysicalAssetType) => {
    mutate(formValue);
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      toast(
        <Alert message="Physical Asset Successfully Added" type="success" />
      );
      onSuccess();
    }
    if (isError) {
      setIsSubmitting(false);
    }
  }, [isSuccess, isError]);

  return (
    <div>
      <InfoModal width="w-[60%]" className="h-fit rounded-md">
        <div className="flex flex-row items-center justify-between border-b p-3 pl-6">
          <p className="text-2xl font-bold text-[#353535]">
            Add Physical Asset
          </p>
          <button type="button" onClick={onClose}>
            <Icon name={Icons.Close} />
          </button>
        </div>
        <form
          autoComplete="false"
          className="p-8"
          onSubmit={formik.handleSubmit}
        >
          {formik.values.physicalAsset.map((physical, index) => (
            <div key={index} className="mb-1 mt-8">
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <CustomSelect
                    options={[
                      { label: 'Fixed Asset', value: 'Fixed Asset' },
                      { label: 'Movable Asset', value: 'Movable Asset' },
                      {
                        label: 'Other Valuable Asset',
                        value: 'Other Valuable Asset'
                      }
                    ]}
                    selectedOption={{
                      value: physical.assetType,
                      label: physical.assetType
                    }}
                    handleOptionChange={option => {
                      formik.setFieldValue(
                        `physicalAsset[${index}].assetType`,
                        option?.value
                      );
                    }}
                    label="Asset Type"
                    name="physicalAsset"
                    isRequired
                    isSearchable
                    className="h-[3.5rem] rounded-2xl"
                    error={
                      formik.touched.physicalAsset
                        ? formik.errors.physicalAsset?.[index] &&
                          formik.errors.physicalAsset.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.physicalAsset[index])
                            ).physicalAsset
                          : ''
                        : ''
                    }
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`physicalAsset[${index}].description`}
                    placeholder=""
                    value={physical.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Description of Asset"
                    error={
                      formik.touched.physicalAsset
                        ? formik.errors.physicalAsset?.[index] &&
                          formik.errors.physicalAsset.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.physicalAsset[index])
                            ).description
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 md:gap-6">
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`physicalAsset[${index}].location`}
                    placeholder=""
                    value={physical.location}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Location of Asset"
                    error={
                      formik.touched.physicalAsset
                        ? formik.errors.physicalAsset?.[index] &&
                          formik.errors.physicalAsset.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.physicalAsset[index])
                            ).location
                          : ''
                        : ''
                    }
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
                <div className="group relative z-0 mb-6 w-full">
                  <TextField
                    name={`physicalAsset[${index}].regNo`}
                    placeholder=""
                    value={physical.regNo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    label="Asset Unique ID/Reg No"
                    error={
                      formik.touched.physicalAsset
                        ? formik.errors.physicalAsset?.[index] &&
                          formik.errors.physicalAsset.length > 0
                          ? JSON.parse(
                              JSON.stringify(formik.errors.physicalAsset[index])
                            ).regNo
                          : ''
                        : ''
                    }
                    isRequired
                    size="lg"
                    className="rounded-2xl border-[1.5px] border-gray-300"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between gap-2">
            <div className="flex w-full items-center gap-2">
              <Icon name={Icons.Required} />
              <span className="text-[10px] text-[#7A7C87]">
                Indicates Required
              </span>
            </div>
          </div>

          <div className="mt-5 flex w-full items-end justify-end">
            <Button
              variant="primary"
              size="custom"
              className="mt-5 rounded p-3"
              label="Add new Physical Asset"
              type="submit"
              disabled={!formik.isValid}
              loading={isSubmitting || isLoading}
            ></Button>
          </div>
        </form>
      </InfoModal>
    </div>
  );
}
